import { Activity, BookOpen, CalendarDays, ChartNoAxesCombined, ChevronRight, CreditCard, FolderOpen, Globe2, Home, LogOut, MessageCircle, Settings, Sparkles, UserRound, Users, WalletCards } from "lucide-react";
import { Children, cloneElement, Fragment, isValidElement, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { api } from "../lib/api";
import type { User, UserRole } from "../types";
import { AvatarPicker } from "./AvatarPicker";
import { AccountSettings } from "./AccountSettings";
import { GuidedTour } from "./GuidedTour";
import { Logo } from "./Logo";
import { Loading } from "./Loading";
import { WorkspaceNavigation } from "./WorkspaceNavigation";
import { NotificationCenter } from "./NotificationCenter";

const iconMap = { Home, BookOpen, Sparkles, Users, MessageCircle, CreditCard, ChartNoAxesCombined, Settings, CalendarDays, WalletCards, FolderOpen, Activity, UserRound };
type IconName = keyof typeof iconMap;
export type NavItem = { label: string; labelAr?: string; icon: IconName; href: string };

const roleNames: Record<UserRole, string> = { student: "Learner studio", parent: "Parent view", instructor: "Instructor desk", admin: "Command center" };
const roleNamesAr: Record<UserRole, string> = { student: "استوديو المتعلم", parent: "مساحة ولي الأمر", instructor: "مكتب المحاضر", admin: "مركز إدارة المنصة" };

export function Protected({ role, children }: { role: UserRole; children: ReactNode }) {
  const { user, isRestoring } = useAuth();
  const { isArabic } = useLanguage();
  const location = useLocation();
  if (isRestoring) return <Loading label={isArabic ? "جارٍ استعادة جلستك…" : "Restoring your Kidix session…"} />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(`${location.pathname}${location.search}${location.hash}`)}`} replace />;
  if (user.role !== role) return <Navigate to={`/app/${user.role}`} replace />;
  return children;
}

function sectionFromHref(href: string) { return href.startsWith("#") ? href.slice(1) : href.split("/").filter(Boolean).at(-1) ?? "overview"; }

const legacySectionAliases: Partial<Record<UserRole, Record<string, string>>> = {
  student: { recorded: "library" },
  admin: {
    leads: "admissions",
    payments: "finance",
    "payment-reviews": "finance",
    messages: "communications",
    audit: "governance",
    "contact-inbox": "communications",
  },
};

export function resolveDashboardSection({
  role,
  sections,
  pathSection,
  hash,
}: {
  role: UserRole;
  sections: string[];
  pathSection?: string;
  hash?: string;
}) {
  const overview = sections[0] ?? "overview";
  const hashSection = hash?.replace(/^#/, "") ?? "";
  const requestedSection =
    pathSection === overview && hashSection ? hashSection : pathSection;
  const mappedSection =
    (requestedSection
      ? legacySectionAliases[role]?.[requestedSection]
      : undefined) ?? requestedSection;
  if (mappedSection && sections.includes(mappedSection)) return mappedSection;
  return pathSection && pathSection !== overview ? "access-denied" : overview;
}

function canTraverseElement(element: ReactElement) {
  return typeof element.type === "string" || element.type === Fragment;
}

function sectionIds(node: ReactNode, valid: Set<string>, found = new Set<string>()) {
  Children.forEach(node, (child) => {
    if (!isValidElement(child)) return;
    if (!canTraverseElement(child)) return;
    const props = child.props as { id?: string; children?: ReactNode; "data-dashboard-section"?: string };
    if (props["data-dashboard-section"] && valid.has(props["data-dashboard-section"])) found.add(props["data-dashboard-section"]);
    if (props.id && valid.has(props.id)) found.add(props.id);
    if (props.children) sectionIds(props.children, valid, found);
  });
  return found;
}

function containsSection(node: ReactNode, active: string, valid: Set<string>): boolean {
  let found = false;
  Children.forEach(node, (child) => {
    if (found || !isValidElement(child)) return;
    if (!canTraverseElement(child)) return;
    const props = child.props as { id?: string; children?: ReactNode; "data-dashboard-section"?: string };
    if (props["data-dashboard-section"] === active) { found = true; return; }
    if (props.id === active && valid.has(props.id)) {
      found = true;
      return;
    }
    if (props.children) found = containsSection(props.children, active, valid);
  });
  return found;
}

function hideNestedSections(node: ReactNode, active: string, valid: Set<string>): ReactNode {
  return Children.map(node, (child) => {
    if (!isValidElement(child)) return child;
    if (!canTraverseElement(child)) return child;
    const element = child as ReactElement<{ id?: string; children?: ReactNode; hidden?: boolean }>;
    const shouldHide = !!element.props.id && valid.has(element.props.id) && element.props.id !== active && !containsSection(element.props.children, active, valid);
    const nested = element.props.children ? hideNestedSections(element.props.children, active, valid) : element.props.children;
    return cloneElement(element, shouldHide || element.props.hidden ? { hidden: true } : {}, nested);
  });
}

function dashboardView(children: ReactNode, active: string, valid: Set<string>, overview: string): ReactNode {
  return Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    const root = child as ReactElement<{ className?: string; children?: ReactNode }>;
    if (!root.props.className?.includes("dashboard-page")) return child;
    const visibleChildren = Children.map(root.props.children, (part) => {
      if (!isValidElement(part)) return active === overview ? part : null;
      if ((part.props as { "data-dashboard-persistent"?: string })["data-dashboard-persistent"] === "true") return part;
      const ids = sectionIds(part, valid);
      const visible = active === overview ? ![...ids].some((id) => id !== overview) : ids.has(active);
      if (!visible) return null;
      return hideNestedSections(part, active, valid);
    });
    return cloneElement(root, { "data-active-section": active } as Record<string, string>, visibleChildren);
  });
}

function ProfileTab({ user, roleName, isArabic, learnerCodeSignIn, onAvatar, onSettings, onLogout, onUpdateUser }: { user: User; roleName: string; isArabic: boolean; learnerCodeSignIn?: boolean; onAvatar: () => void; onSettings: () => void; onLogout: () => void; onUpdateUser: (user: User) => void }) {
  const [name, setName] = useState(user.name);
  const updateProfile = useMutation({
    mutationFn: (nextName: string) => api<User>("/account/profile", { method: "PATCH", body: JSON.stringify({ name: nextName }) }),
    onSuccess: onUpdateUser,
  });
  const tr = (en: string, ar: string) => isArabic ? ar : en;
  const hasChanges = name.trim() !== user.name;
  const ProfileHeading = user.role === "parent" ? "h2" : "h1";

  return <section className="dashboard-page profile-tab" id="profile">
    <header className="profile-tab__hero">
      <button className="profile-tab__avatar" type="button" onClick={onAvatar} aria-label={tr("Change profile image", "تغيير صورة الملف الشخصي")}>
        <img src={user.avatarUrl ?? ""} alt="" />
        <span><Settings /></span>
      </button>
      <div>
        <span className="eyebrow">{tr("Your Kidix account", "حسابك في كيدكس")}</span>
        <ProfileHeading>{user.name}</ProfileHeading>
        <p>{roleName}{!learnerCodeSignIn && <> · <bdi>{user.email}</bdi></>}</p>
      </div>
    </header>

    <div className="profile-tab__grid">
      <form className="profile-card profile-card--details" onSubmit={(event) => { event.preventDefault(); if (hasChanges) updateProfile.mutate(name.trim()); }}>
        <div className="profile-card__heading"><div><span className="eyebrow">{tr("Profile", "الملف الشخصي")}</span><h2>{tr("Your information", "معلوماتك")}</h2></div></div>
        <label>{tr("Display name", "الاسم الظاهر")}<input value={name} onChange={(event) => setName(event.target.value)} minLength={2} maxLength={120} required /></label>
        {!learnerCodeSignIn && <label>{tr("Email address", "البريد الإلكتروني")}<input value={user.email} disabled dir="ltr" /></label>}
        <p className="profile-card__hint">{learnerCodeSignIn ? tr("Use your learner code and PIN to sign in. Ask your family if you need help.", "استخدم رمز المتعلم ورقم PIN لتسجيل الدخول. اطلب المساعدة من أسرتك إذا احتجت إليها.") : tr("Your email address is used to sign in and cannot be changed here.", "يُستخدم بريدك الإلكتروني لتسجيل الدخول ولا يمكن تغييره من هنا.")}</p>
        {updateProfile.isError && <p className="form-error" role="alert">{tr("We couldn't save your profile. Please try again.", "تعذر حفظ ملفك الشخصي. حاول مرة أخرى.")}</p>}
        <button className="button button--dark" type="submit" disabled={!hasChanges || updateProfile.isPending}>{updateProfile.isPending ? tr("Saving…", "جارٍ الحفظ…") : tr("Save changes", "حفظ التغييرات")}</button>
      </form>

      <div className="profile-tab__side">
        <article className="profile-card profile-card--account">
          <span className="eyebrow">{tr("Account", "الحساب")}</span>
          <h2>{roleName}</h2>
          <p>{tr("Manage your profile picture, notifications, and support preferences from one place.", "أدر صورة ملفك الشخصي والإشعارات وتفضيلات الدعم من مكان واحد.")}</p>
          <button className="profile-card__action" type="button" onClick={onAvatar}>{tr("Change profile image", "تغيير صورة الملف الشخصي")} <ChevronRight /></button>
          <button className="profile-card__action" type="button" onClick={onSettings}>{tr("Account settings & support", "إعدادات الحساب والدعم")} <ChevronRight /></button>
        </article>
        <article className="profile-card profile-card--signout">
          <span className="eyebrow">{tr("Session", "الجلسة")}</span>
          <h2>{tr("Ready to leave?", "هل تريد المغادرة؟")}</h2>
          <p>{tr("Signing out keeps your account safe on shared devices.", "تسجيل الخروج يحافظ على أمان حسابك على الأجهزة المشتركة.")}</p>
          <button className="button button--danger" type="button" onClick={onLogout}><LogOut /> {tr("Sign out", "تسجيل الخروج")}</button>
        </article>
      </div>
    </div>
  </section>;
}

export function DashboardShell({ nav, children, learnerId, profileContent, learnerCodeSignIn }: { nav: NavItem[]; children: ReactNode; learnerId?: string; profileContent?: ReactNode; learnerCodeSignIn?: boolean }) {
  const { user, logout, updateUser } = useAuth();
  const { isArabic, toggleLanguage } = useLanguage();
  const location = useLocation();
  const parentLearner = new URLSearchParams(location.search).get("learner") ?? learnerId;
  const selectedCourse = new URLSearchParams(location.search).get("courseId") ?? location.pathname.match(/^\/app\/student\/recorded\/([0-9a-f-]{36})$/i)?.[1];
  const navigation = useRef<HTMLElement>(null);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const supportRequested = new URLSearchParams(location.search).get("support") === "1";
  useEffect(() => { if (supportRequested) setSettingsOpen(true); }, [supportRequested]);
  const profileItem: NavItem = { label: "Profile", labelAr: "الملف الشخصي", icon: "UserRound", href: "#profile" };
  const dashboardNav = user?.role === "parent" ? nav : [...nav.filter((item) => sectionFromHref(item.href) !== "profile"), profileItem];
  const sections = dashboardNav.map((item) => sectionFromHref(item.href));
  const sectionSet = useMemo(() => new Set(sections), [sections.join("|")]);
  const pathParts = location.pathname.split("/").filter(Boolean);
  const pathSection = pathParts[2];
  const activeSection = user?.role === "parent" && pathSection === "payments" ? "learning" : resolveDashboardSection({
    role: user?.role ?? (pathParts[1] as UserRole),
    sections,
    pathSection,
    hash: location.hash,
  });
  const overview = sections[0] ?? "overview";
  useEffect(() => {
    navigation.current?.querySelector<HTMLAnchorElement>("a.active")?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [activeSection]);
  if (!user) return null;
  const roleName = isArabic ? roleNamesAr[user.role] : roleNames[user.role];
  const profile = <><ProfileTab user={user} roleName={roleName} isArabic={isArabic} learnerCodeSignIn={user.role === "student" && learnerCodeSignIn} onAvatar={() => setAvatarOpen(true)} onSettings={() => setSettingsOpen(true)} onLogout={() => void logout()} onUpdateUser={updateUser} />{profileContent}</>;
  const view = user.role === "parent" ? <>{children}{pathSection === "account" && new URLSearchParams(location.search).get("panel") === "profile" && profile}</> : activeSection === "profile" ? profile : activeSection === "access-denied" ? <section className="dashboard-page" role="alert"><div className="notice notice--error"><h1>{isArabic ? "هذه الصفحة غير متاحة لحسابك" : "This page is not available to your account"}</h1><p>{isArabic ? "قد تحتاج إلى صلاحية مختلفة. عد إلى مساحتك أو تواصل مع مسؤول كيديكس إذا كنت تتوقع الوصول إليها." : "You may need a different permission. Return to your workspace or contact a Kidix administrator if you expected access."}</p><Link className="button button--dark" to={`/app/${user.role}/${overview}`}>{isArabic ? "العودة إلى المساحة" : "Return to workspace"}</Link></div></section> : dashboardView(children, activeSection, sectionSet, overview);
  const tourSteps = [
    { selector: ".sidebar nav", title: "Your workspace, page by page", titleAr: "مساحتك، صفحة بصفحة", body: "Each main area now has its own URL. Use this navigation to move without losing your place.", bodyAr: "لكل قسم رئيسي رابط وصفحة مستقلة. استخدم القائمة للتنقل دون أن تفقد مكانك." },
    { selector: ".notification-trigger", title: "Nothing important gets buried", titleAr: "كل تحديث مهم في مكانه", body: "New messages and account updates arrive here. Open an item to mark it read and go to its page.", bodyAr: "تظهر الرسائل والتحديثات هنا. افتح أي إشعار لتحديده كمقروء والانتقال إلى صفحته." },
    { selector: ".sidebar__profile", title: "Make the workspace yours", titleAr: "اجعل المساحة تخصك", body: "Choose a Kidix character or upload your own profile image at any time.", bodyAr: "اختر شخصية من كيديكس أو ارفع صورتك الشخصية في أي وقت." },
  ];

  return <div className={`dashboard dashboard--${user.role}`}>
    <a href="#workspace-content" className="skip-link">{isArabic ? "انتقل إلى المحتوى" : "Skip to content"}</a>
    <aside className="sidebar">
      <Logo light />
      <div className="sidebar__role"><span>{roleName}</span><small>{isArabic ? "مساحة كيديكس" : "Kidix workspace"}</small></div>
      <WorkspaceNavigation items={dashboardNav} role={user.role} activeSection={activeSection} navigationRef={navigation} search={user.role === "parent" && parentLearner ? `?learner=${encodeURIComponent(parentLearner)}` : (user.role === "student" || user.role === "instructor") && selectedCourse ? `?courseId=${encodeURIComponent(selectedCourse)}` : ""} />
      <button className="sidebar__profile" onClick={() => setAvatarOpen(true)}><img src={user.avatarUrl ?? ""} alt="" /><span><b>{user.name}</b><small>{isArabic ? "تغيير الصورة" : "Change profile image"}</small></span><Settings /></button>
      <button className="sidebar__logout" onClick={() => void logout()}><LogOut /><span>{isArabic ? "تسجيل الخروج" : "Sign out"}</span></button>
    </aside>
    <div className="dashboard__body">
      <header className="topbar"><Logo compact /><div><span className="eyebrow">{roleName}</span><b>{isArabic ? `أهلاً بك، ${user.name.split(" ")[0]}` : `Good to see you, ${user.name.split(" ")[0]}`}</b></div><div className="topbar__actions"><button className="icon-button" aria-label={isArabic ? "الإعدادات والدعم" : "Settings and support"} onClick={() => setSettingsOpen(true)}><Settings /></button><button className="language-switch" onClick={toggleLanguage}><Globe2 /> {isArabic ? "EN" : "العربية"}</button><NotificationCenter /></div></header>
      <main id="workspace-content" tabIndex={-1}>{user.role !== "parent" && user.role !== "admin" && !["overview", "today", "profile", "access-denied"].includes(activeSection) && !location.pathname.includes("/recorded/") && <h1 className="sr-only">{isArabic ? dashboardNav.find(item => sectionFromHref(item.href) === activeSection)?.labelAr ?? roleName : dashboardNav.find(item => sectionFromHref(item.href) === activeSection)?.label ?? roleName}</h1>}{view}</main>
    </div>
    <AvatarPicker open={avatarOpen} onClose={() => setAvatarOpen(false)} />
    <AccountSettings open={settingsOpen} onClose={() => setSettingsOpen(false)} initialTab={supportRequested ? "support" : "security"} />
    <GuidedTour tourId={`dashboard-${user.role}`} steps={tourSteps} autoStart={false} />
  </div>;
}

export function MetricCard({ label, value, note, tone = "mint" }: { label: string; value: string | number; note: string; tone?: string }) {
  return <article className={`metric metric--${tone}`}><span>{label}</span><strong>{value}</strong><small>{note}</small></article>;
}

export function SectionHeading({ eyebrow, title, action }: { eyebrow?: string; title: string; action?: ReactNode }) {
  return <div className="section-heading"><div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h2>{title}</h2></div>{action}</div>;
}
